import { Toaster } from "@/shared/ui/toaster";
import { Toaster as Sonner } from "@/shared/ui/sonner";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { ThemeProvider } from "next-themes";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Suspense, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/features/auth/components/AuthContext";
import { SimpleModeProvider } from "@/contexts/SimpleModeContext";
import { DataFetchStrategyProvider } from "@/contexts/DataFetchStrategyContext";
import { SplashScreen } from "@/components/global/SplashScreen";
import { GlobalLoadingProvider } from "@/components/global/GlobalLoading";
import { ConfirmDialogProvider } from "@/components/global/ConfirmDialog";
import { CaptureCardProvider } from "@/features/capture/CaptureCardProvider";
import { OutboxProvider } from "@/features/outbox/OutboxProvider";
import { ErrorBoundary } from "@/components/routing/ErrorBoundary";
import { ProtectedRoute } from "@/components/routing/ProtectedRoute";
import { OfflineRouteBoundary } from "@/app/routing/OfflineRouteBoundary";
import { InstallPrompt, UpdateBanner } from "@/components/global/InstallPrompt";
import { OrgBrandingApplier } from "@/components/global/OrgBrandingApplier";
import { queryClient } from "@/shared/lib/queryClient";
import { getLanguageFont } from "@/shared/i18n/config";
import { lazyWithRetry, prefetchCriticalRoutes, registerRouteChunk } from "@/shared/lib/routePrefetch";
import { useAudioPackPrefetch } from "@/features/agriagent/lib/audioCache";
import { useOfflinePhotoPolicy } from "@/shared/hooks/useOfflinePhotoPolicy";
import { TrainingSync } from "@/features/help/components/TrainingSync";

// Screen-specific skeleton loaders
import {
  HomeScreenSkeleton,
  CameraScreenSkeleton,
  ScoreScreenSkeleton,
  AgentScreenSkeleton,
  NetworkScreenSkeleton,
  SettingsScreenSkeleton,
  SatelliteScreenSkeleton,
  AnalyticsScreenSkeleton,
  GenericScreenSkeleton,
} from "@/components/loading/ScreenSkeletons";

// ============= Lazy-loaded Screens =============
// Priority 1: Core navigation screens (prefetched)
const Index = lazyWithRetry(() => import("./pages/Index"));
const CameraScreen = lazyWithRetry(() => import("@/features/capture/screens/CameraScreen").then(m => ({ default: m.CameraScreen })));
const ScoreScreen = lazyWithRetry(() => import("@/features/score/screens/ScoreScreen").then(m => ({ default: m.ScoreScreen })));
const AgriAgentScreen = lazyWithRetry(() => import("@/features/agriagent/screens/AgriAgentScreen").then(m => ({ default: m.AgriAgentScreen })));

// Priority 2: Secondary navigation screens
const NetworkScreen = lazyWithRetry(() => import("@/features/community/screens/NetworkScreen"));
const ActivityLogScreen = lazyWithRetry(() => import("@/features/capture/screens/ActivityLogScreen").then(m => ({ default: m.ActivityLogScreen })));
// ProfileScreen removed — /profile now redirects to /settings
const PhotosScreen = lazyWithRetry(() => import("@/features/capture/screens/PhotosScreen").then(m => ({ default: m.PhotosScreen })));

// Priority 3: Settings and auth
const SettingsScreen = lazyWithRetry(() => import("@/features/settings/screens/SettingsScreen").then(m => ({ default: m.SettingsScreen })));
const CropPickerScreen = lazyWithRetry(() => import("@/features/onboarding/screens/CropPickerScreen").then(m => ({ default: m.CropPickerScreen })));
const AuthScreen = lazyWithRetry(() => import("@/features/auth/screens/AuthScreen").then(m => ({ default: m.AuthScreen })));
const SignUpScreen = lazyWithRetry(() => import("@/features/auth/screens/SignUpScreen").then(m => ({ default: m.SignUpScreen })));
const BridgeScreen = lazyWithRetry(() => import("@/features/auth/screens/BridgeScreen").then(m => ({ default: m.BridgeScreen })));
const PublicLegalScreen = lazyWithRetry(() => import("@/features/consent/screens/PublicLegalScreen").then(m => ({ default: m.PublicLegalScreen })));
const OnboardingFlow = lazyWithRetry(() => import("./features/onboarding/OnboardingFlow").then(m => ({ default: m.OnboardingFlow })));
const AAKConsentJourney = lazyWithRetry(() => import("./features/consent/AAKConsentJourney").then(m => ({ default: m.AAKConsentJourney })));
const ConsentManageScreen = lazyWithRetry(() => import("@/features/consent/screens/ConsentManageScreen").then(m => ({ default: m.ConsentManageScreen })));
const ReConsentGate = lazyWithRetry(() => import("./features/consent/ReConsentGate").then(m => ({ default: m.ReConsentGate })));

// Priority 4: Heavy feature screens (loaded on-demand)
const SatelliteScreen = lazyWithRetry(() => import("@/features/satellite/screens/SatelliteScreen").then(m => ({ default: m.SatelliteScreen })));
const AnalyticsScreen = lazyWithRetry(() => import("@/features/analytics/screens/AnalyticsScreen").then(m => ({ default: m.AnalyticsScreen })));
const KnowledgeScreen = lazyWithRetry(() => import("@/features/knowledge/screens/KnowledgeScreen").then(m => ({ default: m.KnowledgeScreen })));
const KnowledgeAssetDetailScreen = lazyWithRetry(() =>
  import("@/features/knowledge/screens/KnowledgeAssetDetailScreen").then((m) => ({
    default: m.KnowledgeAssetDetailScreen,
  })),
);
const USSDScreen = lazyWithRetry(() => import("@/features/ussd/screens/USSDScreen").then(m => ({ default: m.USSDScreen })));
// TVTool B7a: the Help screen the question mark opens (D10)
const HelpScreen = lazyWithRetry(() => import("@/features/help/screens/HelpScreen").then(m => ({ default: m.HelpScreen })));

// Priority 5: Financial screens (Loans & Banks embedded in MoneyScreen)
const MoneyScreen = lazyWithRetry(() => import("@/features/money/screens/MoneyScreen").then(m => ({ default: m.MoneyScreen })));
const NewCycleScreen = lazyWithRetry(() => import("@/features/cycles/screens/NewCycleScreen").then(m => ({ default: m.NewCycleScreen })));
const PastCyclesScreen = lazyWithRetry(() => import("@/features/cycles/screens/PastCyclesScreen").then(m => ({ default: m.PastCyclesScreen })));
const CycleDetailScreen = lazyWithRetry(() => import("@/features/cycles/screens/CycleDetailScreen").then(m => ({ default: m.CycleDetailScreen })));

// Misc screens
const NotificationsScreen = lazyWithRetry(() => import("@/features/notifications/screens/NotificationsScreen"));
const CustomerSupportScreen = lazyWithRetry(() => import("@/features/help/screens/CustomerSupportScreen"));
const PrivacyPolicyScreen = lazyWithRetry(() => import("@/features/consent/screens/PrivacyPolicyScreen"));
const IntegrityPolicyScreen = lazyWithRetry(() => import("@/features/consent/screens/IntegrityPolicyScreen"));
const TermsOfServiceScreen = lazyWithRetry(() => import("@/features/consent/screens/TermsOfServiceScreen"));
const DailyRecordScreen = lazyWithRetry(() => import("@/features/capture/screens/DailyRecordScreen").then(m => ({ default: m.DailyRecordScreen })));
const SubmissionSuccessScreen = lazyWithRetry(() => import("@/features/capture/screens/SubmissionSuccessScreen").then(m => ({ default: m.SubmissionSuccessScreen })));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const MicroInteractionsShowcase = lazyWithRetry(() => import("./components/dev/MicroInteractionsShowcase").then(m => ({ default: m.MicroInteractionsShowcase })));

// Register routes for prefetching
registerRouteChunk('/', () => import("./pages/Index"));
registerRouteChunk('/camera', () => import("@/features/capture/screens/CameraScreen"));
registerRouteChunk('/score', () => import("@/features/score/screens/ScoreScreen"));
registerRouteChunk('/agent', () => import("@/features/agriagent/screens/AgriAgentScreen"));
registerRouteChunk('/network', () => import("@/features/community/screens/NetworkScreen"));
registerRouteChunk('/activity', () => import("@/features/capture/screens/ActivityLogScreen"));
registerRouteChunk('/settings', () => import("@/features/settings/screens/SettingsScreen"));
// /profile redirects to /settings
registerRouteChunk('/photos', () => import("@/features/capture/screens/PhotosScreen"));
registerRouteChunk('/satellite', () => import("@/features/satellite/screens/SatelliteScreen"));
registerRouteChunk('/analytics', () => import("@/features/analytics/screens/AnalyticsScreen"));
registerRouteChunk('/knowledge', () => import("@/features/knowledge/screens/KnowledgeScreen"));
registerRouteChunk('/knowledge/:assetId', () => import("@/features/knowledge/screens/KnowledgeAssetDetailScreen"));
registerRouteChunk('/help', () => import("@/features/help/screens/HelpScreen"));

const AppContent = () => {
  const { i18n } = useTranslation();
  const [showSplash, setShowSplash] = useState(true);

  // Background pre-download of the GEN_AUDIOS voice-note pack once the farmer +
  // country code are known (idle, post-login) → instant + offline playback later.
  useAudioPackPrefetch();

  // A2 (R16): read the offline-photo kill switch at app open (cached; OFF if never read).
  // No behaviour depends on it yet — the Stage B outbox will.
  useOfflinePhotoPolicy();

  useEffect(() => {
    const fontClass = getLanguageFont(i18n.language);
    document.documentElement.className = fontClass;
    
    // Set RTL direction for Urdu (and future Arabic languages)
    const rtlLanguages = ['ur', 'ar'];
    const dir = rtlLanguages.includes(i18n.language) ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Hide splash after minimum display time
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
      // Prefetch critical routes after splash
      prefetchCriticalRoutes();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // SD-O64: a recovered radio warms the farmer-facing chunks again (a new build's chunks are precached by the
  // service worker at install; this keeps the browser's module cache warm for the screens opened most).
  useEffect(() => {
    const warm = () => prefetchCriticalRoutes();
    window.addEventListener('online', warm);
    return () => window.removeEventListener('online', warm);
  }, []);

  return (
    <>
      <AnimatePresence mode="wait">
        {showSplash && <SplashScreen key="splash" />}
      </AnimatePresence>
      <>
        <Toaster />
        <Sonner />
        <OrgBrandingApplier />
        <InstallPrompt />
        <UpdateBanner />
        {/* TVTool B7a (§9.4): on app open the training-video cache is brought in step with the published list. */}
        <TrainingSync />
        
        {/* SD-O64: a screen whose code is not on the phone renders the no-signal screen inside the shell, never the raw error. */}
        <OfflineRouteBoundary>
        <Routes>
          {/* Public routes */}
          <Route path="/auth" element={
            <Suspense fallback={<GenericScreenSkeleton />}>
              <AuthScreen />
            </Suspense>
          } />
          <Route path="/signup" element={
            <Suspense fallback={<GenericScreenSkeleton />}>
              <SignUpScreen />
            </Suspense>
          } />
          {/* F2: PUBLIC legal viewer — pre-login access from the signup consent
              links. The protected /terms and /privacy Settings routes below are
              unchanged. */}
          <Route path="/legal/terms" element={
            <Suspense fallback={<GenericScreenSkeleton />}>
              <PublicLegalScreen docType="terms" />
            </Suspense>
          } />
          <Route path="/legal/privacy" element={
            <Suspense fallback={<GenericScreenSkeleton />}>
              <PublicLegalScreen docType="privacy" />
            </Suspense>
          } />
          {/* WAP Phase 1 (P1.3): bank-wrapper bridge entry — exchanges a one-time
              code for a CCS session, then lands the farmer on Home. Public. */}
          <Route path="/bridge" element={
            <Suspense fallback={<GenericScreenSkeleton />}>
              <BridgeScreen />
            </Suspense>
          } />
          <Route path="/onboarding" element={
            <Suspense fallback={<GenericScreenSkeleton />}>
              <OnboardingFlow />
            </Suspense>
          } />
          
          {/* Protected routes - Priority 1: Core navigation */}
          <Route path="/" element={
            <ProtectedRoute>
              <Suspense fallback={<HomeScreenSkeleton />}>
                <Index />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/camera" element={
            <ProtectedRoute>
              <Suspense fallback={<CameraScreenSkeleton />}>
                <CameraScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/score" element={
            <ProtectedRoute>
              <Suspense fallback={<ScoreScreenSkeleton />}>
                <ScoreScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/agent" element={
            <ProtectedRoute>
              <Suspense fallback={<AgentScreenSkeleton />}>
                <AgriAgentScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          
          {/* Priority 2: Secondary navigation */}
          <Route path="/network" element={
            <ProtectedRoute>
              <Suspense fallback={<NetworkScreenSkeleton />}>
                <NetworkScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/activity" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <ActivityLogScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/activity/:id" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <ActivityLogScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/profile" element={<Navigate to="/settings" replace />} />
          <Route path="/notifications" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <NotificationsScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/photos" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <PhotosScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          
          {/* AAK Consent journey */}
          <Route path="/aak-consent" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <AAKConsentJourney />
              </Suspense>
            </ProtectedRoute>
          } />

          {/* Per-purpose consent withdrawal (SD-PERPURPOSE-WITHDRAW) */}
          <Route path="/consent-manage" element={
            <ProtectedRoute>
              <Suspense fallback={<SettingsScreenSkeleton />}>
                <ConsentManageScreen />
              </Suspense>
            </ProtectedRoute>
          } />

          {/* Login-time re-consent gate (SD-RECONSENT-GATE). Excluded from the
              gate redirect in ProtectedRoute (like /onboarding). */}
          <Route path="/re-consent" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <ReConsentGate />
              </Suspense>
            </ProtectedRoute>
          } />

          {/* Priority 3: Settings */}
          <Route path="/settings" element={
            <ProtectedRoute>
              <Suspense fallback={<SettingsScreenSkeleton />}>
                <SettingsScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/crop-picker" element={
            <ProtectedRoute>
              <Suspense fallback={<SettingsScreenSkeleton />}>
                <CropPickerScreen />
              </Suspense>
            </ProtectedRoute>
          } />

          {/* Priority 4: Heavy features */}
          <Route path="/satellite" element={
            <ProtectedRoute>
              <Suspense fallback={<SatelliteScreenSkeleton />}>
                <SatelliteScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute>
              <Suspense fallback={<AnalyticsScreenSkeleton />}>
                <AnalyticsScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/knowledge" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <KnowledgeScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/knowledge/:assetId" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <KnowledgeAssetDetailScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/ussd" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <USSDScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          {/* TVTool B7a: Help screen — "Ask us" + the training videos (D10) */}
          <Route path="/help" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <HelpScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          
          {/* Priority 5: Financial — /loans and /banks redirect to /money */}
          <Route path="/loans" element={<Navigate to="/money" replace />} />
          <Route path="/banks" element={<Navigate to="/money" replace />} />
          <Route path="/money" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <MoneyScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/cycle/new" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <NewCycleScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/cycle/history" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <PastCyclesScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/cycle/:id" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <CycleDetailScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/support" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <CustomerSupportScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/privacy" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <PrivacyPolicyScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/terms" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <TermsOfServiceScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/integrity" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <IntegrityPolicyScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/daily-log" element={<Navigate to="/daily-record" replace />} />
          <Route path="/daily-record" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <DailyRecordScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="/submission-success" element={
            <ProtectedRoute>
              <Suspense fallback={<GenericScreenSkeleton />}>
                <SubmissionSuccessScreen />
              </Suspense>
            </ProtectedRoute>
          } />
          
          {/* Dev routes */}
          <Route path="/dev/interactions" element={
            <Suspense fallback={<GenericScreenSkeleton />}>
              <MicroInteractionsShowcase />
            </Suspense>
          } />
          
          {/* 404 */}
          <Route path="*" element={
            <Suspense fallback={<GenericScreenSkeleton />}>
              <NotFound />
            </Suspense>
          } />
        </Routes>
        </OfflineRouteBoundary>
      </>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SimpleModeProvider>
            <DataFetchStrategyProvider>
              <AuthProvider>
                <GlobalLoadingProvider>
                  <ConfirmDialogProvider>
                    <CaptureCardProvider>
                      {/* FIX-CCS-OUTBOX: the offline photo outbox and its sync runner (inside AuthProvider: owner binding). */}
                      <OutboxProvider>
                        <TooltipProvider>
                          <AppContent />
                        </TooltipProvider>
                      </OutboxProvider>
                    </CaptureCardProvider>
                  </ConfirmDialogProvider>
                </GlobalLoadingProvider>
              </AuthProvider>
            </DataFetchStrategyProvider>
          </SimpleModeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;